import React, { FunctionComponent, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { SurveyMode } from '@reducers/SurveyReducer'
import { NavigationContext } from '@context/NavigationContext'
import { ConsentContext } from '@context/ConsentContext'
import { SurveyContext } from '@context/SurveyContext'
import { SurveyStep } from '@models/Survey/SurveyStep'
import { TabbedPane } from '@components/Survey/TabbedPane/TabbedPane'
import AnswerInput from '@components/AnswerInput/AnswerInput'
import { isIntegratedApp } from '@models/AppType'
import {
  categoryHasMultipleSurveysFromSurvey,
  categoryHasProbabilityForecasting,
  categorySkipsPredictionStep,
  getCategory,
  isLastSurveyInCategory,
} from '@utils/categoryFlow'

export function Answer() {
  const { t } = useTranslation('Answer')
  const { setPrevHandler, setNextHandler } = useContext(NavigationContext)
  const { hasConsented, checkConsents, hasAutoFill } = useContext(ConsentContext)
  const {
    currentQuestion,
    currentPosition,
    currentAnswer,
    questionsCount,
    previousIndex,
    nextIndex,
    setAnswer,
    surveyIsCompleted,
    survey,
    hasAcknowledged,
    setPreviousQuestion,
    setNextQuestion,
    setMode,
    setNextStep,
    autofillSurvey,
    questionnairePhase,
    submitAnswers,
    setResultVariant,
  } = useContext(SurveyContext)

  useEffect(() => {
    setMode(SurveyMode.DEFAULT)

    // We disable the navigation once this component is destroyed
    return () => {
      setPrevHandler(undefined)
      setNextHandler(undefined)
    }
  }, [])

  useEffect(() => {
    // We check if we already consented to be sure
    if (!hasConsented) {
      checkConsents()
    }
  }, [hasConsented])

  useEffect(() => {
    if (hasAutoFill) {
      autofillSurvey()
    }
  }, [hasAutoFill])

  const handlePrevQuestion = () => setPreviousQuestion()
  const handleNextQuestion = () => {
    const lastQuestion = surveyIsCompleted && currentPosition === questionsCount
    if (lastQuestion) {
      const category = getCategory(survey)
      const multiSurvey = categoryHasMultipleSurveysFromSurvey(survey)
      const lastInCategory = isLastSurveyInCategory(survey)
      const keepElectoralTunnel =
        categoryHasProbabilityForecasting(category) && lastInCategory

      // SCAN26-style multi-Q final (no probability/forecasting): go straight to results.
      // EQUE26-style final: keep Prediction → Probability → Forecasting before results.
      if (multiSurvey && lastInCategory && !keepElectoralTunnel) {
        setResultVariant('final')
        submitAnswers().then(() => setNextStep(SurveyStep.Results))
        return
      }

      if (hasAcknowledged !== undefined) {
        if (categorySkipsPredictionStep(category, survey)) {
          setResultVariant(lastInCategory ? 'final' : 'intermediate')
          submitAnswers().then(() => setNextStep(SurveyStep.Results))
        } else {
          setNextStep(SurveyStep.Prediction)
        }
        return
      }
      if (isIntegratedApp()) {
        return setNextStep(SurveyStep.Acknowledge)
      }

      return setNextStep(SurveyStep.Email)
    }
    return setNextQuestion()
  }
  useEffect(() => {
    setPrevHandler(previousIndex !== undefined ? handlePrevQuestion : undefined)
    setNextHandler(
      currentAnswer != null && (surveyIsCompleted || nextIndex !== undefined) ? handleNextQuestion : undefined,
    )
  }, [currentAnswer, surveyIsCompleted, previousIndex, nextIndex])

  const tabs = [
    {
      text: t('navigation.title'),
    },
  ]
  return (
    <TabbedPane
      tabs={tabs}
      headerText={
        currentPosition ? t('navigation.progress', { sup: currentPosition ?? 0, sub: questionsCount ?? 0 }) : undefined
      }
      propClassName="Answer">
      {currentQuestion && (
        <AnswerInput
          key={currentQuestion.id}
          question={currentQuestion}
          answer={currentAnswer}
          onSelection={setAnswer}
        />
      )}
    </TabbedPane>
  )
}

export default Answer
